<script setup lang="ts">
import { computed, onMounted, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import TagAliases from '@/components/tagSettings/TagAliases.vue'
import TagArrangement from '@/components/tagSettings/TagArrangement.vue'
import TagDetail from '@/components/tagSettings/TagDetail.vue'
import TagImplied from '@/components/tagSettings/TagImplied.vue'
import TagList from '@/components/tagSettings/TagList.vue'
import TagTips from '@/components/tagSettings/TagTips.vue'
import { useTagEditor } from '@/composables/useTagEditor'
import { parseTagsPath, tagsPath } from '@/settings/tagsNav'
import { findTagByName } from '@/todos/tags'

// The tags category of the Settings (`/settings/tags[/<id>[/aliases|implied|arrangement]]`): picks the page from the route.
// The tags are read when the category opens; `?tag=<name>` (the link on a tag page) scrolls to the row of that tag or alias
// and marks it for a moment, a name without a row (only in use on todos) does nothing.
const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const editor = useTagEditor()
const view = computed(() => parseTagsPath(route.params.rest))
watchEffect(() => {
  if (view.value.view === 'unknown') void router.replace(tagsPath())
})

onMounted(async () => {
  await editor.load()
  const name = route.query.tag
  const found = typeof name === 'string' ? findTagByName(editor.tags.value, name) : undefined
  if (found && view.value.view === 'list') await editor.markTag(found.id)
})
</script>

<template>
  <p v-if="view.view !== 'unknown'" class="ui-hint page-hint">
    {{ t(`settings.tags.pageHint.${view.view}`) }}
  </p>
  <TagList v-if="view.view === 'list'" />
  <TagTips v-else-if="view.view === 'tips'" />
  <TagDetail v-else-if="view.view === 'tag'" :tag-id="view.id" />
  <TagAliases v-else-if="view.view === 'aliases'" :tag-id="view.id" />
  <TagImplied v-else-if="view.view === 'implied'" :tag-id="view.id" />
  <TagArrangement v-else-if="view.view === 'arrangement'" :tag-id="view.id" />
</template>
